import React, { useEffect, useRef, useState } from 'react';
import { useAction, useMutation, useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useBetterAuthConvexAuth } from '../hooks/useAppAuth';
import Layout from '../components/Layout/Layout';
import MobileReadingsTable from '../components/Dashboard/MobileReadingsTable';
import ReadingsChart from '../components/Dashboard/ReadingsChart';
import TabbedSection from '../components/Dashboard/TabbedSection';
import SavingsCalculator from '../components/Dashboard/SavingsCalculator';
import UpdateDetailsModal from '../components/Verification/UpdateDetailsModal';
import SitePopup from '../components/Popup/SitePopup';

const DashboardPage: React.FC = () => {
  const { convexUser } = useBetterAuthConvexAuth();
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const syncExternalReadings = useAction(api.externalReadings.syncMyExternalReadings);
  const hasSyncedRef = useRef(false);

  // Fetch data using Convex queries
  const stats = useQuery(api.readings.getMyStats);
  const weeklyReadings = useQuery(api.readings.getMyWeekly);
  const updatelastUsersLogin = useMutation(api.users.updateLastLogin);
  const monthlyReadings = useQuery(api.readings.getMyMonthly);

  // Check if user needs to update details
  useEffect(() => {
    if (convexUser) {
      updatelastUsersLogin({ userId: convexUser._id })
      const lastUpdate = new Date(convexUser.lastDetailsUpdate);
      const daysSinceUpdate = Math.floor(
        (Date.now() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24)
      );

      if (daysSinceUpdate >= 365) {
        setShowUpdateModal(true);
      }
    }
  }, [convexUser]);

  // Sync latest readings from external API (throttled server-side)
  useEffect(() => {
    if (!convexUser || hasSyncedRef.current) return;
    hasSyncedRef.current = true;

    syncExternalReadings({}).catch((error) => {
      console.error('Failed to sync external readings', error);
    });
  }, [convexUser, syncExternalReadings]);

  const loading = stats === undefined || weeklyReadings === undefined || monthlyReadings === undefined;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
        </div>
      </Layout>
    );
  }

  // Transform readings for components (add id field for compatibility)
  const formattedWeekly = weeklyReadings?.map((r: { _id: string; date: string; dailyKwh: number; totalKwh: number }) => ({
    id: r._id,
    date: r.date,
    daily_kwh: r.dailyKwh,
    total_kwh: r.totalKwh,
  })) || [];

  const formattedMonthly = monthlyReadings?.map((r: { _id: string; date: string; dailyKwh: number; totalKwh: number }) => ({
    id: r._id,
    date: r.date,
    daily_kwh: r.dailyKwh,
    total_kwh: r.totalKwh,
  })) || [];

  return (
    <Layout>
      {/* Site-specific popup */}
      {convexUser && <SitePopup userId={convexUser._id} />}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        <h1 className="text-2xl sm:text-3xl font-light mb-1 sm:mb-2">
          Welcome {convexUser?.name || convexUser?.username}
        </h1>
        {convexUser?.username && (
          <p className="text-sm text-gray-500 mb-6 sm:mb-8">
            Site reference: {convexUser.username.toUpperCase()}
          </p>
        )}

        {stats && <TabbedSection stats={stats} />}

        <div className="mb-8 sm:mb-12">
          <h2 className="text-xl sm:text-2xl font-light mb-4 sm:mb-6">Table - Previous 7 days</h2>
          <MobileReadingsTable readings={formattedWeekly} />
        </div>

        <div className="mb-8 sm:mb-12">
          <h2 className="text-xl sm:text-2xl font-light mb-4 sm:mb-6">Graph - Previous 31 Days</h2>
          <ReadingsChart readings={formattedMonthly} />
        </div>

        <SavingsCalculator totalGenerated={stats?.total_generated_kwh || 0} />

        <div className="mt-6 sm:mt-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
          <a
            href="https://www.ashadegreener.co.uk/how-does-solar-work"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#16A34A]  text-white text-center py-4 px-4 rounded-md hover:bg-shade-green-700 transition-colors text-sm sm:text-base min-h-[48px] flex items-center justify-center"
          >
            How do solar panels work?
          </a>
          <a
            href="https://www.ashadegreener.co.uk/make-the-most"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#16A34A]  text-white text-center py-4 px-4 rounded-md hover:bg-shade-green-700 transition-colors text-sm sm:text-base min-h-[48px] flex items-center justify-center"
          >
            Make the most of the free energy
          </a>
          <a
            href="https://www.ashadegreener.co.uk/sell-your-home-solar"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#16A34A]  text-white text-center py-4 px-4 rounded-md hover:bg-shade-green-700 transition-colors text-sm sm:text-base min-h-[48px] flex items-center justify-center sm:col-span-2 md:col-span-1"
          >
            Selling your home?
          </a>
        </div>
      </div>

      {showUpdateModal && (
        <UpdateDetailsModal
          onClose={() => setShowUpdateModal(false)}
          onUpdate={() => {
            setShowUpdateModal(false);
            window.location.reload();
          }}
        />
      )}
    </Layout>
  );
};

export default DashboardPage;
